import { UserRole } from "constants/userRole";
import { useOptionalUserWithAccount } from "context/UserAccountProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetCustomsByOrderTypeAndAccountIdQuery } from "store/queries/customize";
import { useGetOrderQuery, useGetOrdersQuery } from "store/queries/order";
import { useGetPayersByAccountQuery } from "store/queries/payers";
import {
  useGetMyTrainingQuery,
  useSaveMyTrainingMutation,
} from "store/queries/training";
import { userSelector } from "store/slices/user";
import { getTourSession, subscribeTourSession } from "training/engine/session";
import { toTourOrder } from "training/engine/tourOrder";
import { useTour } from "training/engine/useTour";
import { ITourAccount, ITourContext, TourRoute } from "training/types";
import { IAccount } from "types/Account/Account";
import { IOverviewPage } from "types/Customize";
import { IOrder } from "types/Order/Order";
import { ITrainingProgress } from "types/Training";

import { TourPopoverGlobalStyle } from "./TourPopover";

/** The query parameter that starts the tour (the banner's fallback link, a support link). */
export const TRAINING_QUERY_PARAM = "training";

function toTourAccount(
  account: IAccount | undefined
): ITourAccount | undefined {
  if (!account) return undefined;
  return {
    directPayEnabled: account.directPayEnabled,
    orderTypesVisible: account.orderTypesVisible,
    paymentPlanTypeEnabled: account.paymentPlanTypeEnabled,
    partialPaymentsEnabled: account.partialPaymentsEnabled,
    eligibilityEnabled: !!account.providers?.find(
      (provider) => !!provider.mainFacility
    )?.eligibilityEnabled,
    paymentProcessor: account.finixMerchantId ? "finix" : "stripe",
    features: account.features,
  };
}

/**
 * The trainee's estimate id, newest source first: the create page's event (this session), the progress the
 * engine last wrote or handed over, then the saved progress. Re-read whenever the session changes.
 */
function useTrainingOrderId(
  saved: ITrainingProgress | null | undefined
): string | undefined {
  const read = () => {
    const session = getTourSession();
    return (
      session.order?.id ??
      session.handoff?.progress.orderId ??
      session.progress?.orderId ??
      undefined
    );
  };
  const [fromSession, setFromSession] = useState<string | undefined>(read);
  useEffect(() => subscribeTourSession(() => setFromSession(read())), []);
  return fromSession ?? saved?.orderId ?? undefined;
}

/**
 * CLE-3015 — mounts the tour on one page: the hospital view (`route="estimates"`, all its tabs), the create
 * page (`route="create"`), the trainee's Estimate Details page (`route="estimate"`, with `orderId`) and
 * the patient page a staff user opens from it (`route="patient"`, with `orderExternalId`).
 *
 * It starts the tour when, and only when, the user is HospitalStaff (never a SuperAdmin), not in a
 * reports-only or contracts-only mode, on an account with `features.clearCertifiedTraining` on, not yet
 * certified, and the page's data has loaded — and then one of:
 * - the URL carries `?training=1` (removed once read, so a reload does not restart it);
 * - the session holds a hand-off to this page (the tour was running on the previous page);
 * - the banner's Start / Resume was pressed (`requestTrainingTourStart`).
 *
 * The trainee's estimate (PR 3): once Create and Send has run, its id is in the session or the saved
 * progress, and the host reads the estimate itself (GET /orders/:id) — after a reload the session is empty
 * — and does not start until it has. If that read fails the tour does not start (logged); the banner still
 * offers Resume. On the Estimate Details / patient page the tour runs only on the trainee's OWN estimate: a
 * hand-off that lands on another estimate (the top row was someone else's) is redirected to theirs.
 */
export function TrainingTourHost({
  route,
  accountId,
  orderId,
  orderExternalId,
}: {
  route: TourRoute;
  accountId: string | undefined;
  /** `estimate` route: the id of the estimate the page shows. */
  orderId?: string;
  /** `patient` route: the external id of the estimate the page shows. */
  orderExternalId?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { scope } = useSelector(userSelector);
  const userInfo = useOptionalUserWithAccount()?.userInfo;
  const isHospitalStaff = scope === UserRole.HOSPITAL_STAFF;
  const hasAccount = !!accountId;

  const { data: trainingData } = useGetMyTrainingQuery(accountId, {
    skip: !isHospitalStaff || !hasAccount,
  });
  const record = trainingData?.data;
  const eligible =
    isHospitalStaff &&
    hasAccount &&
    !!record?.enabled &&
    !record.clearCertifiedAt &&
    !userInfo?.reportsOnlyMode &&
    !userInfo?.contractsOnlyMode;

  const { data: accountData } = useGetAccountByIdQuery(accountId ?? "", {
    skip: !eligible,
  });
  // Only the create page needs the payer list (2.5 "Self-Pay Rate").
  const { data: payersData } = useGetPayersByAccountQuery(accountId ?? "", {
    skip: !eligible || route !== "create",
  });
  const trainingOrderId = useTrainingOrderId(record?.trainingProgress);
  const {
    data: orderData,
    isError: orderError,
    isFetching: orderFetching,
  } = useGetOrderQuery(trainingOrderId, {
    skip: !eligible || !trainingOrderId,
  });
  // Only the hospital view needs to know whether the hospital has any estimate (Module 1's row steps).
  // One row is enough: the response's `total` is the count. RTK Query keys the cache on the arguments,
  // so this is one request per account, not one per render.
  const estimatesQuery = useGetOrdersQuery(
    { accountId, page: 0, pageSize: 1 },
    { skip: !eligible || route !== "estimates" }
  );
  const estimatesSettled =
    route !== "estimates" || estimatesQuery.isSuccess || estimatesQuery.isError;
  const estimateCount =
    route === "estimates" && estimatesQuery.data
      ? estimatesQuery.data.total ?? estimatesQuery.data.data?.length ?? 0
      : undefined;
  const [saveMyTraining] = useSaveMyTrainingMutation();

  const payerNames = useMemo<string[] | undefined>(
    () => payersData?.data?.map((payer: { name: string }) => payer.name),
    [payersData]
  );

  const fetchedOrder = orderData?.data as IOrder | undefined;
  // The content the patient page renders from (Overview.tsx, the same query and arguments, so one cached
  // request): it falls back to the default customization, which `order.account.customizes` does not hold.
  // Without it 4.3 said "no expiry" over a page showing one (manual pass 2026-10-26).
  const readsContent =
    eligible &&
    !!fetchedOrder?.orderType &&
    !!fetchedOrder?.accountId &&
    fetchedOrder.id === trainingOrderId;
  const {
    data: contentData,
    isError: contentError,
    isFetching: contentFetching,
  } = useGetCustomsByOrderTypeAndAccountIdQuery(
    {
      orderType: fetchedOrder?.orderType ?? "",
      accountId: fetchedOrder?.accountId ?? "",
    },
    { skip: !readsContent }
  );
  // Undefined until read (or when the read failed: toTourOrder then reads the account's own row); null when
  // the page has no content at all.
  const pageContent: IOverviewPage | null | undefined =
    contentData && !contentFetching
      ? contentData.data?.overviewPage ?? null
      : undefined;
  const contentSettled =
    !readsContent ||
    pageContent !== undefined ||
    (!!contentError && !contentFetching);
  const sessionOrder = getTourSession().order;
  const order = useMemo(() => {
    if (fetchedOrder && fetchedOrder.id === trainingOrderId) {
      try {
        return toTourOrder(fetchedOrder, pageContent);
      } catch (error) {
        console.error("[Clear Certified] could not read the estimate", error);
      }
    }
    // The read failed: the create page's partial copy, if this session has one.
    return sessionOrder?.id === trainingOrderId
      ? sessionOrder ?? undefined
      : undefined;
  }, [fetchedOrder, trainingOrderId, sessionOrder, pageContent]);
  // Settled: no estimate yet, the read came back, or it failed.
  const orderSettled =
    !trainingOrderId ||
    fetchedOrder?.id === trainingOrderId ||
    (orderError && !orderFetching);
  const orderMissing = !!trainingOrderId && orderSettled && !order;

  useEffect(() => {
    if (orderMissing) {
      console.error(
        "[Clear Certified] could not load the trainee's estimate; the tour waits for Resume"
      );
    }
  }, [orderMissing]);

  const context = useMemo<ITourContext>(
    () => ({
      accountId: accountId ?? "",
      route,
      accountCount: userInfo?.accountIds?.length ?? 1,
      analyticsTabEnabled: !!userInfo?.analyticsTabEnabled,
      account: toTourAccount(accountData?.data),
      payerNames,
      order,
      ...(estimateCount !== undefined ? { estimateCount } : {}),
    }),
    [accountId, route, userInfo, accountData, payerNames, order, estimateCount]
  );

  // The Estimate Details / patient page must be showing the trainee's own estimate.
  const onOwnOrder =
    (route !== "estimate" || (!!order && orderId === order.id)) &&
    (route !== "patient" || (!!order && orderExternalId === order.externalId));
  // A failed estimate count does not hold the tour back: unknown counts as no estimates (the card, not
  // the rows).
  const dataReady =
    eligible &&
    !!context.account &&
    estimatesSettled &&
    (route !== "create" || payerNames !== undefined) &&
    orderSettled &&
    contentSettled &&
    !orderMissing;
  const ready = dataReady && onOwnOrder;

  const onSave = useCallback(
    (progress: ITrainingProgress) => saveMyTraining(progress).unwrap(),
    [saveMyTraining]
  );

  const tour = useTour({ context, record, onSave, navigate });
  const { start } = tour;

  // `?training=1`: start once, then drop the parameter.
  const hasParam =
    new URLSearchParams(location.search).get(TRAINING_QUERY_PARAM) === "1";
  useEffect(() => {
    if (!ready || !hasParam) return;
    // Drop the parameter FIRST: `start()` may hand off to the other page and navigate there, and a
    // replace-navigate after it would put the trainee back here with the tour waiting elsewhere.
    const params = new URLSearchParams(location.search);
    params.delete(TRAINING_QUERY_PARAM);
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, {
      replace: true,
    });
    // Arrived from another page (a quiz's "Start Module N"): from the top, like a hand-off below.
    window.scrollTo?.(0, 0);
    start();
  }, [ready, hasParam]);

  // A hand-off from the previous page.
  useEffect(() => {
    if (!dataReady) return;
    const { handoff } = getTourSession();
    if (
      !handoff ||
      handoff.route !== route ||
      handoff.accountId !== accountId
    ) {
      return;
    }
    if (onOwnOrder) {
      // The app does not reset the scroll on a route change: the page would open where the last one was
      // left (the bottom of the create form, deep in the Estimates list), with the first step out of view
      // (manual UX pass: 2.16, 6.4). A hand-off is a new page; it starts at the top.
      window.scrollTo?.(0, 0);
      start({ handoff });
      return;
    }
    // Another estimate (View on a row that was not the trainee's): go to theirs; the hand-off waits.
    if (route === "estimate" && order?.id) {
      navigate(`/orders/${order.id}`, { replace: true });
    } else {
      console.error(
        "[Clear Certified] the tour's next page is not the trainee's estimate; the tour waits for Resume"
      );
    }
  }, [dataReady, onOwnOrder, route, accountId, orderId, orderExternalId]);

  // The banner's Start / Resume.
  const seenStartRequests = useRef(getTourSession().startRequests);
  const readyRef = useRef(ready);
  readyRef.current = ready;
  useEffect(
    () =>
      subscribeTourSession(() => {
        const { startRequests } = getTourSession();
        if (startRequests === seenStartRequests.current) return;
        seenStartRequests.current = startRequests;
        if (readyRef.current) start();
      }),
    [start]
  );

  if (!eligible) return null;
  return (
    <>
      <TourPopoverGlobalStyle />
      {tour.popover}
    </>
  );
}
